/**
 * EmailSection — current email + verification badge + resend verification +
 * email-subscription toggle + change-email flow. Verification, resend, and
 * change all go through the api-server (branded emails with polymyth.quest
 * action links), not Firebase's client SDK / hosted page.
 *
 * Change-email pending state: after successful submit, the input becomes
 * read-only and the button label changes to "Verification sent — check
 * [newEmail]". State persists until the modal closes or the page reloads
 * (no polling). UX mirrors skillful LearnerAccountPage:507-545.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { AlertTriangle, CheckCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/lib/stores/authStore';
import { auth } from '@/lib/firebase';
import { toast } from '@/lib/toast';

function formatDate(isoOrMs: string | number | undefined): string {
  if (!isoOrMs) return '—';
  const date = typeof isoOrMs === 'number' ? new Date(isoOrMs) : new Date(isoOrMs);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function EmailSection({ onClose }: { onClose?: () => void }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [verificationSent, setVerificationSent] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [pendingNewEmail, setPendingNewEmail] = useState<string | null>(null);
  // Set when the server rejects the change with `requires_recent_login`: we then
  // reveal a password field, reauthenticate, and retry with a fresh token.
  const [reauthNeeded, setReauthNeeded] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');

  // Reset local state when the signed-in user changes.
  useEffect(() => {
    setVerificationSent(false);
    setNewEmail('');
    setPendingNewEmail(null);
    setReauthNeeded(false);
    setReauthPassword('');
  }, [user?.uid]);

  // Email-subscription preference (the "Receive emails" toggle, moved into this
  // Email section from the former Notifications tab).
  const [subLoadStatus, setSubLoadStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [subscribed, setSubscribed] = useState(false);
  const [subSaving, setSubSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getEmailSubscription()
      .then(({ subscribed: sub }) => {
        if (!cancelled) {
          setSubscribed(sub);
          setSubLoadStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) setSubLoadStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleSubscription = async (next: boolean) => {
    const prev = subscribed;
    setSubscribed(next);
    setSubSaving(true);
    try {
      const result = await apiClient.putEmailSubscription(next);
      setSubscribed(result.subscribed);
      toast.success(
        result.subscribed
          ? "You're subscribed to Polymyth emails."
          : "You've unsubscribed from Polymyth emails."
      );
    } catch {
      setSubscribed(prev);
      toast.error('Failed to update email preference. Please try again.');
    } finally {
      setSubSaving(false);
    }
  };

  const memberSince = auth.currentUser?.metadata?.creationTime;
  // Password users reauthenticate by password; OAuth (Google) users via popup.
  const isPasswordUser =
    auth.currentUser?.providerData.some((p) => p.providerId === 'password') ?? false;

  if (!user) return null;

  const handleResend = async () => {
    setSendingVerification(true);
    try {
      await apiClient.sendVerificationEmail();
      setVerificationSent(true);
      toast.success('Verification email sent. Check your inbox.');
    } catch {
      toast.error('Failed to send verification email.');
    } finally {
      setSendingVerification(false);
    }
  };

  const applyChangeResult = (result: { ok: boolean; error?: string }, target: string): boolean => {
    if (result.ok) {
      setPendingNewEmail(target);
      setReauthNeeded(false);
      setReauthPassword('');
      toast.success(`Verification link sent to ${target}.`);
      return true;
    }
    if (result.error === 'requires_recent_login') {
      // Changing the account email is account-takeover-sensitive, so the server
      // requires a fresh sign-in. Reveal the password field and let the user
      // reauthenticate, then they resubmit.
      setReauthNeeded(true);
      toast.error('For your security, confirm your password to change your email.');
    } else if (result.error === 'email_in_use') {
      toast.error('That email is already in use by another account.');
    } else if (result.error === 'invalid_email') {
      toast.error('That email address is not valid.');
    } else if (result.error === 'same_email') {
      toast.error('That is already your email.');
    } else {
      toast.error('Failed to send verification to the new address.');
    }
    return false;
  };

  const handleChange = async (e: FormEvent) => {
    e.preventDefault();
    const target = newEmail.trim();
    if (!target) {
      toast.error('Enter a new email address.');
      return;
    }
    if (target.toLowerCase() === (user.email ?? '').toLowerCase()) {
      toast.error('That is already your email.');
      return;
    }
    setChangeSubmitting(true);
    try {
      // If the prior attempt was rejected for a stale session, reauthenticate
      // first so the retried request carries a fresh `auth_time`. A forced token
      // refresh is required because `getIdToken()` otherwise returns the cached
      // (stale-auth_time) token the server would reject again.
      if (reauthNeeded) {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          toast.error('Please sign in again to change your email.');
          return;
        }
        try {
          if (isPasswordUser) {
            const currentEmail = user.email;
            if (!currentEmail || !reauthPassword) {
              toast.error('Enter your current password to continue.');
              return;
            }
            await reauthenticateWithCredential(
              currentUser,
              EmailAuthProvider.credential(currentEmail, reauthPassword)
            );
          } else {
            // OAuth (Google) account: no password — reauthenticate via popup.
            await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
          }
          await currentUser.getIdToken(true);
        } catch {
          toast.error(
            isPasswordUser
              ? 'Incorrect password. Please try again.'
              : 'Google reauthentication failed. Please try again.'
          );
          return;
        }
      }

      const result = await apiClient.requestEmailChange(target);
      applyChangeResult(result, target);
    } catch {
      toast.error('Failed to send verification to the new address.');
    } finally {
      setChangeSubmitting(false);
    }
  };

  // Guests have no email — the change/verify/subscribe UI doesn't apply. Show
  // the guest state and point them to the dashboard "Save your account" card.
  if (user.isAnonymous) {
    return (
      <div className="mx-auto max-w-xl space-y-8">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">Email</h1>
          <p className="text-sm text-muted-foreground">Member since {formatDate(memberSince)}</p>
        </header>
        <div className="rounded-md border border-[hsl(var(--accent-gold))]/40 bg-card p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Mail className="h-4 w-4" />
            No email on this account
          </div>
          <p className="text-sm text-muted-foreground">
            You're playing as a guest — no email has been provided.
          </p>
          <p className="text-sm text-muted-foreground">
            Add an email and password from the <strong>Save your account</strong> card on your
            dashboard to enable sign-in on any device, password recovery, and buying tokens.
          </p>
          <Button
            size="sm"
            className="bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
            onClick={() => {
              onClose?.();
              navigate('/dashboard');
            }}
          >
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Email</h1>
        <p className="text-sm text-muted-foreground">Member since {formatDate(memberSince)}</p>
      </header>

      <div className="space-y-3">
        <Label>Current email</Label>
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 flex items-center gap-2 text-sm">
            <span className="font-medium">{user.email || '—'}</span>
            {user.emailVerified ? (
              <span className="inline-flex items-center gap-1 rounded bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600 dark:text-green-400">
                <CheckCircle className="h-3 w-3" />
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-yellow-500/10 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:text-yellow-400">
                <AlertTriangle className="h-3 w-3" />
                Unverified
              </span>
            )}
          </div>
          {!user.emailVerified && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResend}
              disabled={sendingVerification || verificationSent}
              className="gap-2"
            >
              <Mail className="h-4 w-4" />
              {sendingVerification
                ? 'Sending…'
                : verificationSent
                  ? 'Sent!'
                  : 'Resend verification'}
            </Button>
          )}
        </div>
      </div>

      <form onSubmit={handleChange} className="space-y-3">
        <Label htmlFor="account-new-email">Change email</Label>
        <Input
          id="account-new-email"
          type="email"
          autoComplete="email"
          placeholder="new-email@example.com"
          value={pendingNewEmail ?? newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          readOnly={pendingNewEmail !== null}
          disabled={changeSubmitting || pendingNewEmail !== null}
        />
        {reauthNeeded && pendingNewEmail === null && isPasswordUser && (
          <div className="space-y-1">
            <Input
              id="account-reauth-password"
              type="password"
              autoComplete="current-password"
              placeholder="Current password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              disabled={changeSubmitting}
            />
            <p className="text-xs text-muted-foreground">
              For your security, re-enter your password to change your email.
            </p>
          </div>
        )}
        {reauthNeeded && pendingNewEmail === null && !isPasswordUser && (
          <p className="text-xs text-muted-foreground">
            For your security, you'll confirm with Google when you submit.
          </p>
        )}
        <Button
          type="submit"
          variant="outline"
          size="sm"
          disabled={
            changeSubmitting ||
            pendingNewEmail !== null ||
            !newEmail.trim() ||
            (reauthNeeded && isPasswordUser && !reauthPassword)
          }
        >
          {pendingNewEmail
            ? `Verification sent — check ${pendingNewEmail}`
            : changeSubmitting
              ? 'Sending…'
              : reauthNeeded
                ? 'Confirm & send verification'
                : 'Send verification to new email'}
        </Button>
        {pendingNewEmail && (
          <p className="text-xs text-muted-foreground">
            Click the link in your new inbox, then reload the page.
          </p>
        )}
      </form>

      <div className="space-y-3">
        <Label>Email preferences</Label>
        <div className="rounded-md border border-border bg-card p-4">
          {subLoadStatus === 'loading' && <p className="text-sm text-muted-foreground">Loading…</p>}
          {subLoadStatus === 'error' && (
            <p className="text-sm text-destructive">
              Couldn't load your email preference. Try closing and reopening this panel.
            </p>
          )}
          {subLoadStatus === 'ready' && (
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label
                  htmlFor="email-subscription-toggle"
                  className="text-sm font-medium cursor-pointer"
                >
                  Receive emails from Polymyth
                </Label>
                <p className="text-xs text-muted-foreground">
                  Updates, announcements, and adventure news.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {subSaving && <span className="text-xs text-muted-foreground">Saving…</span>}
                <Switch
                  id="email-subscription-toggle"
                  checked={subscribed}
                  onCheckedChange={handleToggleSubscription}
                  disabled={subSaving}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
